<script lang="ts">
  import { InboxWorkflowPreconfigurationBox } from "@kontextwerk/control-web-frame"
  import {
    type InboxWorkflowJson,
    type InboxWorkflowPreconfigurationProps,
  } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
  import {
    notifyInboxWorkflowPreconfigurationApplyResult,
    resolveInboxWorkflowPreconfigurationTargets,
  } from "@kontextwerk/control-web-frame"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { propertyCustomerRoleOptions } from "../../../../../../../../../../../../_shared/_logic/customer-roles"
  import {
    KEEP_PRECONFIGURATION_VALUE,
    isKeepPreconfigurationChoice,
    withKeepPreconfigurationOption,
    type PreconfigurationChoice,
  } from "../../../../../../../../../../../../_shared/_logic/preconfiguration-keep"
  import type {
    VerifyCustomerRole,
    VerifyCustomerRow,
    VerifyCustomerDecision,
    VerifyCustomerDraft,
  } from "../../contract"

  type VerifyPreconfigurationPatch = Partial<VerifyCustomerDraft> & InboxWorkflowJson

  let {
    rows,
    selectedIds,
    draftFor,
    patchDraft,
    disabled = false,
  }: InboxWorkflowPreconfigurationProps<VerifyCustomerRow, VerifyCustomerDraft> = $props()

  // Decision and role start per row from matches and submitted data, so both keep by default.
  let decision = $state<PreconfigurationChoice<VerifyCustomerDecision>>(KEEP_PRECONFIGURATION_VALUE)
  let role = $state<PreconfigurationChoice<VerifyCustomerRole>>(KEEP_PRECONFIGURATION_VALUE)

  const targetResolution = $derived(
    resolveInboxWorkflowPreconfigurationTargets({
      rows,
      selectedIds,
      getId: (row) => row.actionCaseId,
      isEligible: (row) => !row.disabled,
    }),
  )

  const patchFor = (row: VerifyCustomerRow): VerifyPreconfigurationPatch | null => {
    const rolePatch = isKeepPreconfigurationChoice(role) ? {} : { role }

    if (isKeepPreconfigurationChoice(decision)) return rolePatch

    if (decision === "use_existing") {
      const option = row.customerOptions?.[0]

      if (!option?.customerId) return null

      return {
        decision,
        customerId: option.customerId,
        role: option.role || (isKeepPreconfigurationChoice(role) ? draftFor(row).role : role),
        email: option.email || "",
        phone: option.phone || "",
      }
    }

    return {
      decision,
      customerId: null,
      ...rolePatch,
    }
  }

  const apply = () => {
    let applied = 0
    let skipped = targetResolution.skippedRows.length
    for (const row of targetResolution.targetRows) {
      const patch = patchFor(row)

      if (!patch) {
        skipped += 1
        continue
      }

      if (Object.keys(patch).length) patchDraft(row, patch)

      applied += 1
    }
    notifyInboxWorkflowPreconfigurationApplyResult({
      total: targetResolution.targetRows.length + targetResolution.skippedRows.length,
      applied,
      skipped,
      reason:
        decision === "use_existing"
          ? "Für diese Zeilen gibt es keinen passenden bestehenden Ansprechpartner."
          : undefined,
    })
  }
</script>

<InboxWorkflowPreconfigurationBox
  selectedCount={selectedIds.length}
  targetCount={targetResolution.targetRows.length}
  {disabled}
  onApply={apply}
>
  <div class="preconfiguration-grid">
    <Input
      type="select"
      label="Als was speichern?"
      bind:value={decision}
      brightBackground={false}
      surfaceTone="dark"
      options={withKeepPreconfigurationOption([
        { value: "create_new", name: "Neu anlegen" },
        { value: "use_existing", name: "Vorhandenen zuordnen" },
        { value: "skip", name: "Ignorieren" },
      ])}
    />
    <Input
      type="select"
      label="Welche Rolle hat der Ansprechpartner?"
      bind:value={role}
      brightBackground={false}
      surfaceTone="dark"
      options={withKeepPreconfigurationOption(
        propertyCustomerRoleOptions.map((option) => ({
          value: option.value,
          name: option.name,
        })),
      )}
    />
  </div>
</InboxWorkflowPreconfigurationBox>

<style>
  .preconfiguration-grid {
    padding-top: 1rem;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, minmax(12rem, 1fr));
  }
</style>
